import type { User, UserRole } from "@/schemas/user.schema";
import { api } from "@/services/api";

export const membersApi = {
  /**
   * Create a new organization member
   *
   * AI-196 Phase 2: For project-scope roles (developer, member, viewer, guest),
   * project_ids is required to specify which projects the user can access.
   * For org-scope roles (admin), project_ids is optional/ignored.
   * All project IDs are now real UUIDs (including Default Workspace).
   *
   * @param role - The role to assign to the user
   * @param user - User data (name, email, password, etc.)
   * @param options - Additional options (notify_by_email)
   * @param projectIds - Project IDs to associate the user with (required for project-scope roles)
   */
  async create(
    role: UserRole,
    user: Omit<User, "id">,
    options: {
      notify_by_email: boolean;
    },
    projectIds?: string[]
  ) {
    const { data } = await api.post<User>("/user-organizations", {
      role,
      user,
      options,
      project_ids: projectIds,
    });

    return data;
  },

  async updateRole(userId: string, role: UserRole) {
    await api.patch(`/user-organizations/${userId}`, { role });
  },

  async updateStatus(userId: string, status: User["status"]) {
    await api.post(`/user-organizations/${userId}/status`, { status });
  },

  async invite(
    email: string,
    role: UserRole,
    projectIds?: string[]
  ) {
    const { data } = await api.post<User>("/user-organizations", {
      role,
      user: { email },
      options: { notify_by_email: true },
      project_ids: projectIds,
    });

    return data;
  },

  async resendInvite(userId: string) {
    await api.post(`/user-organizations/${userId}/resend-invite`);
  },

  async delete(userId: string) {
    await api.delete(`/user-organizations/${userId}`);
  },

  async restoreByEmail(email: string) {
    await api.post(`/user-organizations/${email}/restore`);
  },

  /**
   * AI-196 Phase 2: Bulk update user's project associations
   *
   * Replaces all project associations for a user with the provided list.
   * All project IDs are now real UUIDs (including Default Workspace).
   *
   * @param userId - The user's ID
   * @param projectIds - Array of project IDs
   */
  async changePassword(userId: string, password: string) {
    await api.post(`/user-organizations/${userId}/change-password`, { password });
  },

  async updateUserProjects(
    userId: string,
    projectIds: string[],
  ): Promise<void> {
    await api.post(`/user-organizations/${userId}/projects`, {
      project_ids: projectIds,
    });
  },
};
